'use client';

import { Flex, freddyTheme, Link, Typography } from '@coolblue-development/freddy';
import cancelProductPlusConsentTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/cancelProductPlusConsentTrigger';
import closeOverlayTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/closeOverlayTrigger';
import confirmProductPlusConsentTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/confirmProductPlusConsentTrigger';
import deselectProductPlusTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/deselectProductPlusTrigger';
import openOverlayTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/openOverlayTrigger';
import pickProductPlusTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/pickProductPlusTrigger';
import readTermsAndConditionsTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/readTermsAndConditionsTrigger';
import selectItemTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/selectItemTrigger';
import selectTermsAndConditionsTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/selectTermsAndConditionsTrigger';
import showProductPlusTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/showProductPlusTrigger';
import { TrackImmediate, useAnalytics } from '@coolblue-development/next-analytics/client';
import { InfoModal, InfoUSP, InteractiveBlock } from '@coolblue-development/next-patterns/client';
import { PriceFormatter } from '@coolblue-development/next-sales-price/client';
import { ShoppingCartItem } from '@coolblue-development/next-shopping-cart';
import type {
  FileCollection,
  MappedPromotionProductUrl
} from '@features/product/components/ProductPlus/types';
import { ProductPlusPromotion } from '@features/product/typings';
import type { PickParameterPropertyType } from '@hooks/PickParameterPropertyType';
import { useTranslation } from '@i18n/useTranslation';
import { capitalizeStringFirstLetter } from '@utils/utils';
import { useState } from 'react';

type TrackingSubFeature = PickParameterPropertyType<typeof openOverlayTrigger, 'sub_feature'>;

export const ProductPlusBlock = ({
  promotions,
  shoppingCartItems = [],
  hideInfoUspIcon = false,
  mappedPromotionProductUrls,
  disableShowTracking = false
}: {
  promotions: ProductPlusPromotion[];
  shoppingCartItems?: ShoppingCartItem[];
  hideInfoUspIcon?: boolean;
  mappedPromotionProductUrls?: MappedPromotionProductUrl[];
  disableShowTracking?: boolean;
}) => {
  const { t } = useTranslation('webshop.productPlus');
  const { trackEvent } = useAnalytics();

  const [productPlusProductId, setProductPlusProductId] = useState<number | null>(null);
  const [isChecked, setIsChecked] = useState(false);
  const [open, setOpen] = useState(false);

  const promotion = promotions[0];
  const promotionType = promotion?.type;

  if (!promotionType) {
    return null;
  }

  const isNespresso = promotionType.toLowerCase() === 'nespresso';
  const isNorton = promotionType.toLowerCase() === 'norton';
  const isFreeWith = promotionType.toLowerCase() === 'freewith';
  const isFreeWithNoConsent = isFreeWith && !promotion?.consent;
  const isFreeWithConsentRequired = isFreeWith && promotion?.consent;
  const isActionRequired = isNespresso || isFreeWithConsentRequired;
  const hasPromotion = isNespresso || isNorton || isFreeWith;

  if (!promotion?.products) {
    return null;
  }

  const mappedProducts = promotion.products
    .map((product, index) => {
      if (!product || !product.image?.id) {
        return null;
      }

      return {
        description: product.description ?? '',
        id: product.id ?? index,
        image: {
          collectionName:
            product.image?.collectionName === 'Products'
              ? ('product' as FileCollection)
              : ('content' as FileCollection),
          id: parseInt(product.image?.id),
          label: product.name ?? ''
        },
        listPrice: product.listPrice ?? 0,
        name: product.name ?? '',
        salesPrice: product.priceIncludingVat ?? 0
      };
    })
    .filter(product => product !== null);

  const selectedProduct =
    mappedProducts.find(product => product.id === productPlusProductId) || mappedProducts[0];

  const isPromotionInCart = shoppingCartItems.some(item =>
    item.subItems?.some(
      subItem => subItem.productId && parseInt(subItem.productId) === selectedProduct.id
    )
  );

  if (isPromotionInCart && isNespresso) return <></>;

  const updateCheckedState = (checked: boolean) => {
    if (!checked) {
      setProductPlusProductId(null);
      trackEvent(
        deselectProductPlusTrigger({
          sub_feature,
          index: 1,
          total_indices: 1,
          item_id: productPlusProductId ? productPlusProductId : 0,
          list: ''
        })
      );
    }
    setIsChecked(checked);
  };

  const getPromoValue = () => {
    if (selectedProduct.listPrice !== 0 && selectedProduct.salesPrice !== 0) {
      const promoValue = Math.floor(selectedProduct.listPrice - selectedProduct.salesPrice);
      return (
        <Typography component='span' color={freddyTheme.color.font.subtle} mr={100}>
          <PriceFormatter price={promoValue} size='medium' isFormerPrice withoutStrikethrough />
        </Typography>
      );
    }

    return (
      <Flex rowGap={100}>
        <Typography
          component='span'
          sx={{ span: { marginLeft: freddyTheme.spacing[100] } }}
          color={freddyTheme.color.font.subtle}
        >
          {t('worth')}
          <PriceFormatter
            price={mappedProducts[0].listPrice}
            size='medium'
            isFormerPrice
            withoutStrikethrough
          />
        </Typography>
      </Flex>
    );
  };

  const productUrl = (): string | undefined => {
    const productObject = mappedPromotionProductUrls?.find(
      obj => obj[selectedProduct.id] !== undefined
    );
    return productObject ? productObject[selectedProduct.id] : undefined;
  };

  const getPromoDescriptor = () => {
    return promotion.descriptor.toLowerCase() === 'free' ? t('free') : t('promo');
  };

  const sub_feature: TrackingSubFeature = isNespresso
    ? ('nespresso' as const)
    : isNorton
      ? ('norton' as const)
      : ('vendor_free_with' as const);

  const handleOpen = (open: boolean) => {
    setOpen(open);
    trackEvent(openOverlayTrigger({ sub_feature, content: promotion.title ?? '' }));
  };

  const handleClose = (open: boolean) => {
    setOpen(open);
    trackEvent(closeOverlayTrigger({ sub_feature }));
  };

  const trackSelectProductPlus = (index: number, totalIndices: number, productId: number) => {
    trackEvent(
      pickProductPlusTrigger({
        sub_feature,
        index: index + 1,
        total_indices: totalIndices,
        item_id: productId,
        list: ''
      })
    );
  };

  const trackConsentConfirmed = () => {
    trackEvent(confirmProductPlusConsentTrigger({ sub_feature, status: 'success' }));
  };

  const trackClickCancel = () => {
    trackEvent(cancelProductPlusConsentTrigger({ sub_feature }));
  };

  const trackConsentGiven = () => {
    trackEvent(selectTermsAndConditionsTrigger({ sub_feature }));
  };

  const trackReadTermsAndConditions = (linkTitle: string, linkUrl: string) => {
    trackEvent(
      readTermsAndConditionsTrigger({
        index: 1,
        total_indices: 1,
        url: linkUrl,
        redirect: linkTitle,
        sub_feature
      })
    );
  };

  const trackClickProductTitle = (): void => {
    trackEvent(
      selectItemTrigger({
        sub_feature,
        index: 1,
        total_indices: 1,
        method: 'title',
        item_id: selectedProduct.id,
        correlation_id: ''
      })
    );
  };

  return (
    hasPromotion && (
      <Flex direction='column' gap={200}>
        <InfoUSP
          hideIcon={isNespresso || hideInfoUspIcon}
          productTitle={promotion.shortTitle ?? promotion.title ?? ''}
          promoValue={getPromoValue()}
          {...(isNespresso && {
            customTitle: (
              <Typography fontWeight={freddyTheme.typography.fontWeight.openSans.semiBold}>
                {promotion.shortTitle || promotion.title}
              </Typography>
            )
          })}
          {...(isNorton && {
            onClick: () => handleOpen(true),
            promoDescriptor: capitalizeStringFirstLetter(getPromoDescriptor())
          })}
          {...(selectedProduct.listPrice !== 0 &&
            selectedProduct.salesPrice !== 0 && {
              suffix: t('discount')
            })}
          {...(isFreeWithNoConsent &&
            mappedPromotionProductUrls && {
              customTitle: (
                <Link onClick={() => trackClickProductTitle()} href={productUrl()} target='_blank'>
                  {promotion.shortTitle || promotion.title}
                </Link>
              )
            })}
          {...(isFreeWith && {
            promoDescriptor: capitalizeStringFirstLetter(getPromoDescriptor())
          })}
        />
        {isActionRequired && selectedProduct.image && (
          <InteractiveBlock
            imageId={selectedProduct.image.id.toString()}
            productTitle={selectedProduct.name}
            actionLinkTitle={promotion.cta?.text}
            onClick={() => handleOpen(true)}
            isChecked={isChecked}
            setIsChecked={updateCheckedState}
            salesPrice={selectedProduct.salesPrice}
            listPrice={selectedProduct.listPrice}
            value={selectedProduct.id.toString()}
            name='accessoryPromotion'
          />
        )}
        <InfoModal
          open={open}
          setOpen={handleClose}
          title={promotion.title ?? ''}
          description={promotion.description || ''}
          products={mappedProducts}
          ctaText={t('getOffer')}
          onClickCancel={() => trackClickCancel()}
          onClickLinkInDescription={(linkTitle, linkUrl) => {
            trackReadTermsAndConditions(linkTitle, linkUrl);
          }}
          {...(mappedProducts.length > 1 && {
            onSelectProductPlus: (index, totalIndices, productId) => {
              trackSelectProductPlus(index, totalIndices, productId);
            }
          })}
          {...(isActionRequired && {
            onClick: (productId: string) => {
              setProductPlusProductId(+productId);
              setIsChecked(true);
              setOpen(false);
              trackConsentConfirmed();
            }
          })}
          {...(promotion.consent && {
            consent: {
              description: promotion.consent.description,
              termsConditionsUrl: promotion.consent.termsAndConditionsUrl || '',
              title: promotion.consent.title
            },
            onGiveConsent: () => {
              trackConsentGiven();
            },
            isConsentChecked: isChecked
          })}
          {...(isFreeWith &&
            mappedPromotionProductUrls && {
              url: productUrl(),
              onClickProductTitle: () => trackClickProductTitle()
            })}
        />
        {!disableShowTracking && (
          <TrackImmediate
            trigger={showProductPlusTrigger({
              sub_feature
            })}
          />
        )}
      </Flex>
    )
  );
};
